import { useState } from 'react';

import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalWrapper,
  SelectItem,
  SpeakersPaletteTokens,
} from '@braskai/ui-kit';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';
import { z } from 'zod';

import { useUpgradePlanModalStore } from '@/entities/pricing';
import {
  DiscardChangesModal,
  type Member,
  MemberInvite,
  MemberList,
  RemoveMemberModal,
  SpaceColorCircle,
  spaceColorOptions,
  TeamspaceUpgradeAlert,
  useCreateSpace,
} from '@/entities/teamspace';
import { useCurrentUser, useSettings } from '@/entities/user/currentUser';

import { Form } from '@/shared/ui/form/Form';
import { FormInput } from '@/shared/ui/form/FormInput';
import { FormSelect } from '@/shared/ui/form/FormSelect';
import { ModalHeaderDefault } from '@/shared/ui/modal';

import { useCanInviteMember } from '../model/useCanInviteMember';
import { useCreateTeamspaceModal } from '../model/useCreateTeamspaceModal';

const TeamspaceSchema = z.object({
  name: z.string().min(1, 'Field is required'),
  color: z.string().min(1, 'Field is required'),
});

type FormValue = z.infer<typeof TeamspaceSchema>;

const CreateTeamspaceModalComponent = () => {
  const { data: ownerEmail } = useCurrentUser((user) => user?.email);
  const settings = useSettings();

  const [members, setMembers] = useState<Member[]>([{ email: ownerEmail || '', role: 'owner' }]);
  const [removalMember, setRemovalMember] = useState<null | string>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { isPending: isCreatingSpace, mutateAsync: createSpace } = useCreateSpace();

  const methods = useForm<FormValue>({
    defaultValues: {
      name: '',
      color: spaceColorOptions[1].value,
    },
    resolver: zodResolver(TeamspaceSchema),
  });

  const canInviteMember = useCanInviteMember(members.length);

  const onCloseCreateTeamspaceModal = useCreateTeamspaceModal((state) => state.close);
  const openUpgradePlanModal = useUpgradePlanModalStore((state) => state.open);

  const onContinueEditing = () => setHasUnsavedChanges(false);

  const onClose = () => {
    const name = methods.watch('name');

    if (name || members.length > 1) {
      setHasUnsavedChanges(true);
    } else {
      onCloseCreateTeamspaceModal();
    }
  };

  const onInvite = (email: string) => setMembers((prevState) => [...prevState, { email, role: 'editor' }]);

  const onConfirmDelete = () => {
    setMembers((prevState) => prevState.filter((member) => member.email !== removalMember));
    setRemovalMember(null);
  };

  const onCancelDelete = () => setRemovalMember(null);

  const onSubmit = async (data: FormValue) => {
    await createSpace({
      data,
      emails: members.filter((member) => member.role === 'editor').map((member) => member.email),
    });
    onCloseCreateTeamspaceModal();
  };

  return (
    <Modal size="xs" open onClose={isCreatingSpace ? undefined : onClose}>
      <ModalHeaderDefault
        title="Create new Teamspace"
        isCloseButtonDisabled={isCreatingSpace}
        subtitle="Fill in teamspace details and invite members"
        onClose={onClose}
      />

      <Form {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <ModalContent sx={{ pt: '0!important' }}>
            <Stack gap={20}>
              <Stack direction="row" gap={8}>
                <FormInput label="Teamspace name" name="name" control={methods.control} placeholder="Enter name" />
                <FormSelect
                  label="Color"
                  name="color"
                  sx={{
                    flexShrink: 0,
                    width: '74px',
                  }}
                  control={methods.control}
                  options={spaceColorOptions}
                  renderValue={(value) => <SpaceColorCircle colorToken={value as SpeakersPaletteTokens} />}
                  isMenuInModal
                >
                  {spaceColorOptions.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <SpaceColorCircle colorToken={color.value} />
                    </SelectItem>
                  ))}
                </FormSelect>
              </Stack>
              <Stack gap={12}>
                <MemberInvite isDisabled={!canInviteMember} members={members} onInvite={onInvite} />
                {!canInviteMember && (
                  <TeamspaceUpgradeAlert
                    spacesFeature={settings.scope_settings.available_features.spaces}
                    onClick={() => openUpgradePlanModal({ limitedFeature: 'teamspace' })}
                  />
                )}
                <MemberList email={ownerEmail} members={members} isOwner onDelete={setRemovalMember} />
              </Stack>
            </Stack>
          </ModalContent>
          <ModalActions>
            <Button
              data-testid="create-teamspace-btn"
              disabled={isCreatingSpace}
              isLoading={isCreatingSpace}
              type="submit"
              fullWidth
            >
              Create Teamspace
            </Button>
          </ModalActions>
        </form>
      </Form>
      <RemoveMemberModal isOpen={!!removalMember} onCancel={onCancelDelete} onRemove={onConfirmDelete} />
      <DiscardChangesModal
        isOpen={hasUnsavedChanges}
        onCancel={onContinueEditing}
        onDiscard={onCloseCreateTeamspaceModal}
      />
    </Modal>
  );
};

export const CreateTeamspaceModal = () => (
  <ModalWrapper hook={useCreateTeamspaceModal}>
    <CreateTeamspaceModalComponent />
  </ModalWrapper>
);
